import { inject, Injectable, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { EmitterService } from '../../../services/emitter.service';
import { AnnotationPluginEventService } from './annotation-plugin-event.service';
import {
  AnnotationService,
  onAddNewAnnotation,
  onCancelAddingAnnotation,
  onCloseActiveAnnotation,
  onDeleteAnnotation,
  onDeleteCommentAnnotation,
  onEditingAnnotation,
  onPluginReady,
  onOpenAnnotation,
  onReplyCommentAnnotation,
  onSaveEditedAnnotation,
  onTimeFormatChange,
  onAnnotationMarkerSelectedActiveClick,
  onAnnotationMarkerSelectedActiveHover,
  onAnnotationMarkerSelectedInActiveClick,
  onAnnotationMarkerSelectedInActiveHover,
  onAnnotationsStateChanged,
  onConversationPluginReady,
} from '../../../services/annotation.service';
import { AnnotationApiService } from './annotation-api.service';
import { AnnotationPrivateCondition } from '../../../interfaces/annotation.interface';
import { MediaPlayerStoreService, MediaPlayerStore } from '../../../services/media-player-store.service';
import { MediaType } from '../../../models/media-player-types';

@Injectable()
export class AnnotationEventSubscriptionService implements OnDestroy {
  protected readonly mpStoreService: MediaPlayerStoreService = inject(MediaPlayerStoreService);
  protected readonly pluginEventService: AnnotationPluginEventService = inject(AnnotationPluginEventService);
  private readonly annotationService: AnnotationService = inject(AnnotationService);
  private readonly annotationApiService: AnnotationApiService = inject(AnnotationApiService);
  private readonly destroy$: Subject<void> = new Subject<void>();

  public init(instanceId: string): void {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) {
      throw new Error(`Failed to get media player store for instanceId: ${instanceId}`);
    }
    onAddNewAnnotation.pipe(takeUntil(this.destroy$)).subscribe({
      next: (args: any) => this.handleAddNewAnnotation(args, store),
    });

    onCancelAddingAnnotation.pipe(takeUntil(this.destroy$)).subscribe({
      next: (args: any) => this.handleCancelAddingAnnotation(args, store),
    });

    onDeleteAnnotation.pipe(takeUntil(this.destroy$)).subscribe({
      next: (args: any) => this.handleDeleteAnnotation(args, store),
    });

    onOpenAnnotation.pipe(takeUntil(this.destroy$)).subscribe({
      next: (args: any) => this.handleOpenAnnotation(args, store),
    });

    onCloseActiveAnnotation.pipe(takeUntil(this.destroy$)).subscribe({
      next: (args: any) => this.handleCloseActiveAnnotation(args, store),
    });

    onReplyCommentAnnotation.pipe(takeUntil(this.destroy$)).subscribe({
      next: (args: any) => this.handleReplyCommentAnnotation(args, store),
    });

    onDeleteCommentAnnotation.pipe(takeUntil(this.destroy$)).subscribe({
      next: (args: any) => this.handleDeleteCommentAnnotation(args, store),
    });

    onAnnotationMarkerSelectedActiveHover.pipe(takeUntil(this.destroy$)).subscribe({
      next: (args: any) => this.handleAnnotationMarkerSelectedActiveHover(args, store),
    });

    onAnnotationMarkerSelectedInActiveHover.pipe(takeUntil(this.destroy$)).subscribe({
      next: (args: any) => this.handleAnnotationMarkerSelectedInActiveHover(args, store),
    });

    onAnnotationMarkerSelectedActiveClick.pipe(takeUntil(this.destroy$)).subscribe({
      next: (args: any) => this.handleAnnotationMarkerSelectedActiveClick(args, store),
    });

    onAnnotationMarkerSelectedInActiveClick.pipe(takeUntil(this.destroy$)).subscribe({
      next: (args: any) => this.handleAnnotationMarkerSelectedInActiveClick(args, store),
    });

    onTimeFormatChange.pipe(takeUntil(this.destroy$)).subscribe({
      next: (args: any) => this.handleTimeFormatChange(args, store),
    });

    onEditingAnnotation.pipe(takeUntil(this.destroy$)).subscribe({
      next: (args: any) => this.handleEditingAnnotation(args, store),
    });

    onSaveEditedAnnotation.pipe(takeUntil(this.destroy$)).subscribe({
      next: (args: any) => this.handleSaveEditedAnnotation(args, store),
    });

    onPluginReady.pipe(takeUntil(this.destroy$)).subscribe({
      next: (args: any) => this.handlePluginReady(args, store),
    });

    onConversationPluginReady.pipe(takeUntil(this.destroy$)).subscribe({
      next: (args: any) => this.handlePluginReady(args, store),
    });

    onAnnotationsStateChanged.pipe(takeUntil(this.destroy$)).subscribe({
      next: (args: any) => this.handleAnnotationsStateChanged(args, store),
    });

    EmitterService.get('IMAGE_ANNOTATION:RESETTED')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          store.updateStore({ imagePristine: false });
        },
      });

    EmitterService.get('IMAGE_ANNOTATION:MOVE_TOGGLED')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          store.updateStore({ imagePristine: false });
        },
      });

    EmitterService.get('IMAGE_ANNOTATION:ZOOMED')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          store.updateStore({ imagePristine: false });
        },
      });

    this.annotationService.privateConditionChange$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (newValue: AnnotationPrivateCondition) => {
        store.updateStore({ privateCondition: newValue });

        this.annotationApiService
          .getAnnotations(instanceId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (data: any) => {
              let filteredAnnotations: any = data;
              if (newValue !== 'ALL') {
                filteredAnnotations = data.filter((a: any) => a.isPublic === (newValue === null));
              }

              store.updateStore({ annotations: filteredAnnotations });
              if (
                store.mediaTypeSignal() === MediaType.VIDEO &&
                store.annotationPluginSignal() &&
                store.playerSignal()?.canvasFabric
              ) {
                store.playerSignal()?.canvasFabric.clear();
                if (store.annotationPluginSignal()?.annotationState) {
                  const plugin: any = store.annotationPluginSignal();
                  if (plugin?.annotationState) {
                    plugin.annotationState.annotations = filteredAnnotations;
                  }
                }
              }
            },
          });
      },
    });
  }

  private handleAddNewAnnotation(args: any, store: MediaPlayerStore): void {
    if (
      !store.playerSignal() ||
      !store.isPlayerReadySignal() ||
      args.instanceId != store.instanceIdSignal() ||
      args.itemId != store.itemIdSignal()
    ) {
      return;
    }

    const player: any = store.playerSignal();
    if (!player.canvasFabric) {
      return;
    }

    const shape: any = JSON.stringify(player.canvasFabric);
    args.annotation = {
      ...args.annotation,
      shape: JSON.parse(shape),
      resolution: {
        height: player.canvasFabric.getHeight(),
        width: player.canvasFabric.getWidth(),
      },
    };

    if (store.annotationPluginSignal() && store.isPlayerReadySignal()) {
      store.annotationPluginSignal()?.fire('newAnnotation', args.annotation, args.itemId);
    }
  }

  private handleCancelAddingAnnotation(args: any, store: MediaPlayerStore): void {
    if (args.instanceId != store.instanceIdSignal() || args.itemId != store.itemIdSignal()) {
      return;
    }
    if (store.annotationPluginSignal() && store.isPlayerReadySignal()) {
      store.annotationPluginSignal()?.fire('cancelAddingAnnotation');
    }
  }

  private handleDeleteAnnotation(args: any, store: MediaPlayerStore): void {
    if (args.instanceId != store.instanceIdSignal() || args.itemId != store.itemIdSignal()) {
      return;
    }
    if (args.annotation && store.annotationPluginSignal()) {
      store.annotationPluginSignal()?.fire('destroyAnnotation', { id: args.annotation.id });
    }
  }

  private handleOpenAnnotation(args: any, store: MediaPlayerStore): void {
    if (args.instanceId != store.instanceIdSignal() || args.itemId != store.itemIdSignal()) {
      return;
    }

    if (this.tryOpenVideoAnnotation(store, args)) {
      return;
    }

    if (this.tryOpenImageOrPageAnnotation(store, args)) {
      return;
    }

    if (this.tryOpenAudioAnnotation(store, args)) {
      return;
    }

    // PDF uses comment plugin, not image plugin, so check PDF first
    if (store.mediaTypeSignal() === 'pdf' && store.annotationPluginSignal() && store.isCommentPluginReadySignal()) {
      this.tryOpenCommentAnnotation(store, args);
      return;
    }

    this.tryOpenCommentAnnotation(store, args);
  }

  private tryOpenVideoAnnotation(store: MediaPlayerStore, args: any): boolean {
    if (
      store.mediaTypeSignal() === MediaType.VIDEO &&
      args.annotation &&
      store.annotationPluginSignal() &&
      store.isVideoPluginReadySignal()
    ) {
      const plugin: any = store.annotationPluginSignal();
      const annotationId: string | undefined = args.annotation?.id;
      if (!annotationId) {
        return false;
      }

      const data = {
        messageId: annotationId,
        messageCreatedAt: args.annotation.comments?.[0]?.meta?.datetime_str,
        itemId: plugin.getItem().id,
        instanceId: plugin.instanceId,
      };

      plugin.fire('annotationClickIn', data);
      store.updateStore({ activeAnnotationId: args.annotation.id });
      return true;
    }
    return false;
  }

  private tryOpenAudioAnnotation(store: MediaPlayerStore, args: any): boolean {
    if (
      store.mediaTypeSignal() === MediaType.AUDIO &&
      store.annotationPluginSignal() &&
      store.isAudioPluginReadySignal()
    ) {
      const plugin: any = store.annotationPluginSignal();
      if (!args.annotation?.id) {
        return false;
      }
      const data = {
        messageId: args.annotation.id,
        messageCreatedAt: args.annotation.comments?.[0]?.meta?.datetime_str,
        itemId: plugin.getItem().id,
        instanceId: plugin.instanceId,
      };
      plugin.fire('annotationClickIn', data);
      return true;
    }
    return false;
  }

  private tryOpenImageOrPageAnnotation(store: MediaPlayerStore, args: any): boolean {
    const isImageLike: boolean =
      store.mediaTypeSignal() === MediaType.IMAGE ||
      store.mediaTypeSignal() === MediaType.CONVERTIBLEIMAGE ||
      !!store.activePageSignal();
    if (isImageLike && store.annotationPluginSignal() && store.isImagePluginReadySignal()) {
      const plugin: any = store.annotationPluginSignal();
      if (!args.annotation?.id) {
        return false;
      }
      const data = {
        messageId: args.annotation.id,
        messageCreatedAt: args.annotation.comments?.[0]?.meta?.datetime_str,
        itemId: plugin.getItem().id,
        instanceId: plugin.instanceId,
      };
      plugin.fire('annotationClickIn', data);
      return true;
    }
    return false;
  }

  private tryOpenCommentAnnotation(store: MediaPlayerStore, args: any): void {
    if (store.annotationPluginSignal() && store.isCommentPluginReadySignal()) {
      const plugin: any = store.annotationPluginSignal();
      if (!args.annotation?.id) {
        return;
      }
      const data = {
        messageId: args.annotation.id,
        messageCreatedAt: args.annotation.comments?.[0]?.meta?.datetime_str,
        itemId: plugin.getItem().id,
        instanceId: plugin.instanceId,
      };
      plugin.fire('annotationClickIn', data);
    }
  }

  private handleCloseActiveAnnotation(args: any, store: MediaPlayerStore): void {
    if (args.instanceId != store.instanceIdSignal() || args.itemId != store.itemIdSignal()) {
      return;
    }
    if (args.annotation && store.annotationPluginSignal() && store.playerSignal() && store.isPlayerReadySignal()) {
      store.annotationPluginSignal()?.fire('closeActiveAnnotation');
    }
  }

  private handleReplyCommentAnnotation(args: any, store: MediaPlayerStore): void {
    if (args.instanceId != store.instanceIdSignal() || args.itemId != store.itemIdSignal()) {
      return;
    }

    const plugin: any = store.annotationPluginSignal();
    if (!this.canReplyToComment(store, args, plugin)) {
      return;
    }

    plugin?.fire('newComment', {
      annotationId: args.data.annotation.id,
      body: args.data.commentBody,
      files: args.data.files,
    });
  }

  private canReplyToComment(store: MediaPlayerStore, args: any, plugin: any): boolean {
    if (!plugin || store.instanceIdSignal() !== args.instanceId) {
      return false;
    }

    if (!args.data?.annotation || !args.data?.commentBody) {
      return false;
    }

    const isMediaReady: boolean =
      (store.mediaTypeSignal() == MediaType.AUDIO && store.isAudioPluginReadySignal()) ||
      (store.mediaTypeSignal() == MediaType.IMAGE && store.isImagePluginReadySignal()) ||
      (store.playerSignal() && store.isPlayerReadySignal()) ||
      !!store.mediaTypeSignal();

    if (!isMediaReady) {
      return false;
    }

    return store.itemIdSignal() === plugin.getItem()?.id;
  }

  private handleDeleteCommentAnnotation(args: any, store: MediaPlayerStore): void {
    if (args.instanceId != store.instanceIdSignal() || args.itemId != store.itemIdSignal()) {
      return;
    }
    if (args.comment && store.annotationPluginSignal()) {
      if (store.mediaTypeSignal() === MediaType.VIDEO && store.playerSignal() && store.isPlayerReadySignal()) {
        store.annotationPluginSignal()?.fire('destroyComment', { id: args.comment.id });
      } else if (
        (store.mediaTypeSignal() === MediaType.AUDIO && store.isAudioPluginReadySignal()) ||
        (store.mediaTypeSignal() === MediaType.IMAGE && store.isImagePluginReadySignal())
      ) {
        store.annotationPluginSignal()?.fire('destroyComment', { id: args.comment.id, annotation: args.annotation });
      }
    }
  }

  private handleAnnotationMarkerSelectedActiveHover(args: any, store: MediaPlayerStore): void {
    if (args.instanceId != store.instanceIdSignal() || args.itemId != store.itemIdSignal()) {
      return;
    }

    // if (store.annotationPluginSignal() && store.isPlayerReadySignal()) {
    //   store.annotationPluginSignal()?.fire('openAnnotationHover', args.annotation);
    // }
  }

  private handleAnnotationMarkerSelectedInActiveHover(args: any, store: MediaPlayerStore): void {
    if (args.instanceId != store.instanceIdSignal() || args.itemId != store.itemIdSignal()) {
      return;
    }
    // if (store.annotationPluginSignal() && store.isPlayerReadySignal()) {
    //   store.annotationPluginSignal()?.fire('closeAnnotationHover');
    // }
  }

  private handleAnnotationMarkerSelectedActiveClick(args: any, store: MediaPlayerStore): void {
    if (args.instanceId != store.instanceIdSignal() || args.itemId != store.itemIdSignal()) {
      return;
    }
    if (store.annotationPluginSignal() && store.isPlayerReadySignal() && store.playerSignal()) {
      store.annotationPluginSignal()?.fire('clickInAnnotation', { annotation: args.annotation });
    }
  }

  private handleAnnotationMarkerSelectedInActiveClick(args: any, store: MediaPlayerStore): void {
    if (args.instanceId != store.instanceIdSignal() || args.itemId != store.itemIdSignal()) {
      return;
    }
    if (store.annotationPluginSignal() && store.isPlayerReadySignal() && store.playerSignal()) {
      store.annotationPluginSignal()?.fire('clickOutAnnotation');
    }
  }

  private handleEditingAnnotation(args: any, store: MediaPlayerStore): void {
    if (args.itemId != store.itemIdSignal() || args.instanceId != store.instanceIdSignal()) {
      return;
    }
    if (store.annotationPluginSignal()) {
      store.annotationPluginSignal()?.fire('editAnnotation', args);
    }
  }

  private handleSaveEditedAnnotation(args: any, store: MediaPlayerStore): void {
    if (args.itemId != store.itemIdSignal() || args.instanceId != store.instanceIdSignal()) {
      return;
    }
    if (store.annotationPluginSignal() && store.playerSignal()) {
      const player: any = store.playerSignal();
      if (player.canvasFabric) {
        const shape: any = JSON.stringify(player.canvasFabric);
        args.data = {
          ...args.data,
          shape: shape,
          resolution: {
            height: player.canvasFabric.getHeight(),
            width: player.canvasFabric.getWidth(),
          },
        };
        store.annotationPluginSignal()?.fire('saveEditedAnnotation', {
          data: args.data,
          annotationId: args.annotationId,
        });
      }
    }
  }

  private handleTimeFormatChange(args: any, store: MediaPlayerStore): void {
    if (args.itemId != store.itemIdSignal()) {
      return;
    }
    if (args.format && store.annotationPluginSignal() && store.playerSignal() && store.isPlayerReadySignal()) {
      store.annotationPluginSignal()?.fire(args.act, { format: args.format });
    }
  }

  private handlePluginReady(args: any, store: MediaPlayerStore): void {
    if (args.instanceId != store.instanceIdSignal() || args.itemId != store.itemIdSignal()) {
      return;
    }
    switch (args.type) {
      case MediaType.VIDEO:
        store.updateStore({ isVideoPluginReady: true, annotationPlugin: args.annotationPlugin });
        break;
      case MediaType.IMAGE:
        store.updateStore({ isImagePluginReady: true, annotationPlugin: args.annotationPlugin });
        break;
      case MediaType.AUDIO:
        store.updateStore({ isAudioPluginReady: true, annotationPlugin: args.annotationPlugin });
        this.pluginEventService.initializeAudioAnnotationsEvents(store.instanceIdSignal());
        break;
      case 'comment':
        store.updateStore({ isCommentPluginReady: true, annotationPlugin: args.annotationPlugin });
        break;
      default:
        break;
    }
    this.pluginEventService.init(store.instanceIdSignal());
  }

  private handleAnnotationsStateChanged(args: any, store: MediaPlayerStore): void {
    // this is triggered when an annotation is added, deleted, or updated
    if (args.instanceId != store.instanceIdSignal() || args.itemId != store.itemIdSignal()) {
      return;
    }
    this.pluginEventService.initializeComments(store.mediaTypeSignal(), store.instanceIdSignal());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
